import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "@/lib/site";
import { CITIES } from "@/lib/locations";
import { BLOG_POSTS } from "@/lib/blog-posts";
import Seo from "@/components/site/Seo";
import Breadcrumbs from "@/components/site/Breadcrumbs";

export default function Directory() {
  return (
    <div data-testid="page-directory">
      <Seo
        path="/directory"
        title="Site Directory — Every Service, Every City"
        description="Full directory of in-home senior care services and cities served by Elite Home Care by Hortense across Southwest Florida."
      />

      <section className="section">
        <div className="container-x">
          <Breadcrumbs
            crumbs={[{ name: "Home", path: "/" }, { name: "Directory" }]}
          />
          <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] max-w-3xl">
            Everything we offer — in one place.
          </h1>
          <p className="mt-5 max-w-2xl text-navy-600 text-lg leading-relaxed">
            A complete index of our in-home care services and the Southwest
            Florida communities we serve. Click any link to learn more.
          </p>

          {/* SERVICES */}
          <div className="mt-16">
            <h2 className="text-3xl font-serif text-navy-900">Services</h2>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`dir-service-${s.slug}`}
                    className="group flex items-center justify-between py-2 border-b border-navy-900/10 text-navy-800 hover:text-gold-600 transition-colors"
                  >
                    <span>{s.title}</span>
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CITIES */}
          <div className="mt-16">
            <h2 className="text-3xl font-serif text-navy-900">
              Cities we serve
            </h2>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2">
              {CITIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/home-care/${c.slug}`}
                    data-testid={`dir-city-${c.slug}`}
                    className="group flex items-center justify-between py-2 border-b border-navy-900/10 text-navy-800 hover:text-gold-600 transition-colors"
                  >
                    <span>Home Care in {c.name}</span>
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* MONEY PAGES — service × city */}
          <div className="mt-16">
            <h2 className="text-3xl font-serif text-navy-900">
              Service by city
            </h2>
            <p className="mt-2 text-sm text-navy-500">
              {SERVICES.length * CITIES.length} dedicated service × city pages.
            </p>
            <div className="mt-6 space-y-10">
              {SERVICES.map((s) => (
                <div key={s.slug}>
                  <h3 className="font-serif text-xl text-navy-900">
                    {s.title}
                  </h3>
                  <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    {CITIES.map((c) => (
                      <li key={`${s.slug}-${c.slug}`}>
                        <Link
                          to={`/services/${s.slug}/${c.slug}`}
                          data-testid={`dir-money-${s.slug}-${c.slug}`}
                          className="text-navy-700 hover:text-gold-600 transition-colors"
                        >
                          {s.title} in {c.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* BLOG */}
          <div className="mt-16">
            <h2 className="text-3xl font-serif text-navy-900">Resources</h2>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              {BLOG_POSTS.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`dir-blog-${p.slug}`}
                    className="group flex items-center justify-between py-2 border-b border-navy-900/10 text-navy-800 hover:text-gold-600 transition-colors"
                  >
                    <span>{p.title}</span>
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
